import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { AuthShell } from "./Login";

export default function ResetPassword() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthShell title={t("reset.title")} eyebrow={t("reset.eyebrow")}>
        <div className="space-y-5" data-testid="reset-missing-token">
          <div className="text-[13px] text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-3 bg-[#ff9c85]/5">
            {t("reset.missingToken")}
          </div>
          <Link
            to="/forgot-password"
            className="btn-pill w-full h-11 justify-center focus-ring"
            data-testid="reset-request-new"
          >
            {t("reset.requestNew")}
          </Link>
        </div>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) {
      setError(t("register.pwShort"));
      return;
    }
    if (pw !== pw2) {
      setError(t("reset.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(t("reset.success"));
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("reset.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("reset.title")} eyebrow={t("reset.eyebrow")}>
      {done ? (
        <div className="text-center space-y-4" data-testid="reset-done">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/5 border border-white/10 mx-auto">
            <CheckCircle2 className="w-5 h-5 text-white/80" />
          </div>
          <div className="font-display text-lg font-black tracking-tight">{t("reset.success")}</div>
          <div className="text-[13px] text-white/60">{t("reset.redirecting")}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <p className="text-[13px] text-white/70 leading-relaxed">{t("reset.body")}</p>
          <div>
            <label htmlFor="rp-pw" className="label-eyebrow block mb-2">
              {t("reset.newPw")}
            </label>
            <input
              id="rp-pw"
              data-testid="reset-new-password"
              type="password"
              required
              autoComplete="new-password"
              className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="rp-pw2" className="label-eyebrow block mb-2">
              {t("reset.confirmPw")}
            </label>
            <input
              id="rp-pw2"
              data-testid="reset-confirm-password"
              type="password"
              required
              autoComplete="new-password"
              className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
            />
          </div>
          {error && (
            <div
              className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5"
              data-testid="reset-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-pill w-full h-11 justify-center focus-ring"
            data-testid="reset-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reset.submit")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
